import { useState, useRef, useEffect } from 'react';
import Dh from '../components/Dh';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import UserProfileMenu from '../components/UserProfileMenu';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '@/utils/env';

/* ── constants ─────────────────────────────────────────── */
const RATE    = 3.6725;   // USD→AED
const MAT     = 5000;     // pre-revenue threshold (AED)
const RUNFULL = 12;       // runway months for 100% runway score
const CASHM   = 3;        // cash-target months
const RUNCRIT = 3;        // critical runway threshold
const CIRC    = 2 * Math.PI * 88; // full-circle arc length (r=88)

type Band = 'Excellent' | 'Good' | 'Stable' | 'Warning' | 'Critical';

const BAND_COLOR: Record<Band, string> = {
  Excellent: '#15824E', Good: '#1976d2', Stable: '#E6A700', Warning: '#FF7043', Critical: '#D7263D',
};
const STATUS: Record<Band, string> = {
  Excellent: 'Strong financial position.',
  Good: 'Healthy — keep it up.',
  Stable: 'Stable — watch the trend.',
  Warning: 'Needs attention.',
  Critical: 'Urgent — act on your runway now.',
};

/* ── helpers ───────────────────────────────────────────── */
function fmt(n: number) { return Math.round(n).toLocaleString('en-US'); }
function parse(s: string) { const v = parseFloat(s.replace(/,/g, '')); return isNaN(v) ? 0 : v; }
function bandFor(s: number): Band {
  if (s >= 85) return 'Excellent';
  if (s >= 70) return 'Good';
  if (s >= 55) return 'Stable';
  if (s >= 35) return 'Warning';
  return 'Critical';
}

/* ── Dirham glyph ──────────────────────────────────────── */

/* ── scoring ───────────────────────────────────────────── */
interface ScoreResult {
  score: number; band: Band; preRev: boolean; profitable: boolean;
  runway: number; netBurn: number; runwayScore: number;
  revScore: number | null; cashHealth: number; cashMonths: number; trend: string;
  avgInput: number; expInput: number;
}

function computeScore(cash: number, avg: number, exp: number, curRev: number, currency: 'AED' | 'USD'): ScoreResult {
  const factor  = currency === 'USD' ? RATE : 1;
  const netBurn = exp - avg;
  const profitable = netBurn <= 0;
  const runway  = profitable ? Infinity : (cash <= 0 ? 0 : cash / netBurn);
  const runwayScore = profitable ? 100 : (cash <= 0 ? 0 : Math.min(100, runway / RUNFULL * 100));
  const preRev  = avg * factor < MAT;
  let trend = 'n/a', revScore: number | null = null;
  if (!preRev && avg > 0) {
    const r = curRev / avg;
    trend    = r >= 1.10 ? 'Growing' : (r < 0.95 ? 'Declining' : 'Flat');
    revScore = trend === 'Growing' ? 100 : (trend === 'Declining' ? 30 : 70);
  }
  const cashHealth = exp <= 0 ? 100 : (cash <= 0 ? 0 : Math.min(100, cash / (CASHM * exp) * 100));
  const rawScore   = preRev
    ? (60 * runwayScore + 40 * cashHealth) / 100
    : (50 * runwayScore + 30 * (revScore ?? 0) + 20 * cashHealth) / 100;
  const score = Math.round(Math.max(0, Math.min(100, rawScore)));
  const band: Band = (!profitable && isFinite(runway) && runway < RUNCRIT) ? 'Critical' : bandFor(score);
  const cashMonths = exp > 0 ? cash / exp : Infinity;
  return { score, band, preRev, profitable, runway, netBurn, runwayScore, revScore, cashHealth, cashMonths, trend, avgInput: avg, expInput: exp };
}

function buildInsights(r: ScoreResult, sym: string) {
  const S: string[] = [], R: string[] = [], I: string[] = [];
  if (r.profitable) {
    S.push("You're profitable — revenue covers your costs.");
  } else if (r.runwayScore >= 75) {
    S.push(`Comfortable runway: ${r.runway.toFixed(1)} months.`);
  } else if (isFinite(r.runway) && r.runway < RUNCRIT) {
    R.push(`Critical runway: only ${r.runway.toFixed(1)} months of cash left.`);
  } else if (r.runwayScore < 45) {
    R.push(`Short runway: ${r.runway.toFixed(1)} months.`);
  }
  if (!r.profitable && r.runwayScore < 75 && r.netBurn > 0 && isFinite(r.runway)) {
    const cut = Math.round(r.netBurn * 0.1 / 1000) * 1000 || 5000;
    I.push(`Each ${sym} ${fmt(cut)}/mo of burn cut adds ~${(cut / r.netBurn * r.runway).toFixed(1)} months of runway.`);
  }
  if (!r.preRev) {
    if (r.revScore === 100)      S.push('Revenue is growing vs your average.');
    else if (r.revScore === 30) { R.push('Revenue is declining vs your average.'); I.push('This month is below your average — worth understanding why.'); }
    else                         I.push('Revenue is flat — a growth push would lift your score.');
  } else {
    I.push('Pre-revenue: your score is driven by runway and cash until revenue starts.');
  }
  if (r.cashHealth >= 75) S.push(`Healthy cash buffer (${isFinite(r.cashMonths) ? r.cashMonths.toFixed(1) : '—'} months of expenses).`);
  else if (r.cashHealth < 45) R.push(`Thin cash buffer (under ${CASHM} months of expenses).`);
  if (!S.length) S.push('No standout strengths yet — start with the basics.');
  if (!R.length) R.push('No major risks flagged this month.');
  if (!I.length) I.push('Keep your figures updated monthly to track the trend.');
  return { S, R, I };
}

/* ── ProgressBar ───────────────────────────────────────── */
function ProgressBar({ label, pct, color, animate }: { label: string; pct: number; color: string; animate: boolean }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginBottom: 5, color: '#17263A' }}>
        <span>{label}</span><span style={{ color: '#42505F' }}>{Math.round(pct)}</span>
      </div>
      <div style={{ height: 9, background: '#E7EAEE', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 999, background: color, width: animate ? `${pct}%` : '0%', transition: 'width 0.7s cubic-bezier(.2,.7,.3,1)' }} />
      </div>
    </div>
  );
}

/* ── Field ─────────────────────────────────────────────── */
function Field({ num, label, val, onChange, sym, eqv }: {
  num: string; label: string; val: string; onChange: (v: string) => void;
  sym: 'AED' | 'USD'; eqv: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <label style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#17263A' }}>
        <span style={{ display: 'inline-flex', width: 19, height: 19, alignItems: 'center', justifyContent: 'center', background: '#FFD54F', color: '#17263A', borderRadius: 6, fontSize: 11, fontWeight: 800, marginRight: 7 }}>{num}</span>
        {label}
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 13, color: '#6E7888', fontSize: 14, fontWeight: 700, pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
          {sym === 'AED' ? <Dh /> : '$'}
        </span>
        <input
          type="number" inputMode="numeric" value={val}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{ width: '100%', border: `1.5px solid ${focused ? '#E6B400' : '#E7EAEE'}`, boxShadow: focused ? '0 0 0 4px #FFF6DC' : 'none', borderRadius: 10, padding: '11px 14px 11px 40px', fontSize: 15, fontWeight: 600, color: '#17263A', background: '#fff', fontFamily: 'inherit', outline: 'none', transition: 'border-color .15s,box-shadow .15s' }}
        />
      </div>
      <div style={{ fontSize: 12, color: '#6E7888', fontWeight: 600, marginTop: 5, minHeight: 16 }}>{eqv}</div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE
════════════════════════════════════════════════════════ */
export default function FinancialHealthCheck() {
  const { user, logout } = useAuth();

  const [currency, setCurrency] = useState<'AED' | 'USD'>('AED');
  const [cashStr,  setCashStr]  = useState('');
  const [avgStr,   setAvgStr]   = useState('');
  const [expStr,   setExpStr]   = useState('');
  const [crStr,    setCrStr]    = useState('');
  const [result,   setResult]   = useState<ScoreResult | null>(null);
  const [dispScore, setDispScore] = useState(0);
  const [barsOn,   setBarsOn]   = useState(false);
  // ISO timestamp of a restored check, so we can say when it was last run.
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const animRef   = useRef<number>(0);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => cancelAnimationFrame(animRef.current), []);

  // Hydrate from the saved check. handleCalc PUTs the result to the user record and
  // the footer promises it is "saved to your profile" — but nothing ever read it back,
  // so returning to the page showed an empty form and looked like the save had failed.
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        });
        if (!resp.ok || cancelled) return;
        const json = await resp.json();
        const f = json?.success ? json.user?.financialHealthCheck : null;
        if (!f || cancelled) return;

        const cur: 'AED' | 'USD' = f.currency === 'USD' ? 'USD' : 'AED';
        setCurrency(cur);
        if (f.cash != null)           setCashStr(String(f.cash));
        if (f.avgRevenue != null)     setAvgStr(String(f.avgRevenue));
        if (f.expenses != null)       setExpStr(String(f.expenses));
        if (f.currentRevenue != null) setCrStr(String(f.currentRevenue));

        // Recomputed from the stored inputs rather than reusing the stored score, so
        // there is exactly one scoring code path and a saved result can't drift from it.
        const r = computeScore(
          Number(f.cash) || 0, Number(f.avgRevenue) || 0,
          Number(f.expenses) || 0, Number(f.currentRevenue) || 0, cur,
        );
        setResult(r);
        setDispScore(r.score);   // show the restored score immediately, no count-up
        setBarsOn(true);
        setSavedAt(typeof f.computedAt === 'string' ? f.computedAt : null);
      } catch { /* offline or API down — leave the form empty rather than blocking it */ }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  function eqv(s: string) {
    const v = parse(s);
    return currency === 'AED' && v > 0 ? `≈ $${fmt(v / RATE)}` : '';
  }

  function handleCalc() {
    const c = parse(cashStr), a = parse(avgStr), e = parse(expStr), cr = parse(crStr);
    const r = computeScore(c, a, e, cr, currency);
    setResult(r);

    // Persist the result to the user's record so it shows in the admin Client 360
    // and can drive next-month reminders (the footer promises "saved to your profile").
    if (user?.uid) {
      try {
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
          body: JSON.stringify({
            financialHealthCheck: {
              score: r.score, band: r.band, currency,
              cash: c, avgRevenue: a, expenses: e, currentRevenue: cr,
              runway: isFinite(r.runway) ? Math.round(r.runway * 10) / 10 : null,
              netBurn: r.netBurn, profitable: r.profitable, preRevenue: r.preRev,
              cashMonths: isFinite(r.cashMonths) ? Math.round(r.cashMonths * 10) / 10 : null,
              computedAt: new Date().toISOString(),
            },
          }),
        }).catch(() => {});
      } catch { /* non-blocking */ }
      setSavedAt(new Date().toISOString());
    }

    setBarsOn(false);
    setDispScore(0);
    cancelAnimationFrame(animRef.current);
    const target = r.score;
    const start  = performance.now();
    function step(now: number) {
      const t    = Math.min((now - start) / 950, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDispScore(Math.round(ease * target));
      if (t < 1) animRef.current = requestAnimationFrame(step);
    }
    animRef.current = requestAnimationFrame(step);
    setTimeout(() => setBarsOn(true), 80);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }

  const col      = result ? BAND_COLOR[result.band] : '#1976d2';
  const arcOff   = CIRC * (1 - dispScore / 100);
  const insights = result ? buildInsights(result, currency === 'AED' ? 'AED' : '$') : null;

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#F5F6F8' }}>
      <Sidebar />
      <main className="flex-1 md:ms-64 overflow-y-auto relative">
        <MobileNav onLogout={logout} />

        {/* ── page header ───────────────────────────────── */}
        <div style={{ background: '#fff', borderBottom: '1px solid #E7EAEE', padding: '18px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, position: 'sticky', top: 0, zIndex: 30 }}>
          <div>
            <h1 style={{ fontWeight: 800, fontSize: 22, color: '#17263A', letterSpacing: '-0.02em' }}>
              Financial Health Check
            </h1>
            <p style={{ color: '#6E7888', fontSize: 13.5, marginTop: 3 }}>
              See how financially healthy your company is today — 4 numbers, under a minute.
            </p>
          </div>
          <UserProfileMenu />
        </div>

        <div style={{ padding: '28px 32px 52px', maxWidth: 940, margin: '0 auto' }}>

          {/* ── intro banner ──────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'linear-gradient(120deg,#10243b,#1b3550)', borderRadius: 16, padding: '20px 24px', marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,213,79,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFD54F" strokeWidth="2"><path d="M3 12h4l2 5 4-12 2 7h6" /></svg>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17, color: '#fff' }}>Check your financial health</div>
              <div style={{ color: '#b9c4d2', fontSize: 13, marginTop: 2 }}>A quick read on your runway, cash and revenue. Free, and just for you.</div>
            </div>
            <span style={{ marginLeft: 'auto', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.18)', color: '#fff', fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 999, whiteSpace: 'nowrap' }}>
              Free · No integration needed
            </span>
          </div>

          {/* Restored-check notice — makes it obvious the prefilled figures are the
              user's own saved numbers rather than leftovers from another session. */}
          {savedAt && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FFF8E2', border: '1px solid #FFE9A8', borderRadius: 12, padding: '11px 16px', marginBottom: 16, flexWrap: 'wrap' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B77F00" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><polyline points="3 3 3 8 8 8" />
              </svg>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: '#17263A' }}>
                Showing your saved check from{' '}
                {new Date(savedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
              <span style={{ fontSize: 13, color: '#6E7888', fontWeight: 600 }}>
                — update any figure and recalculate to refresh it.
              </span>
            </div>
          )}

          {/* ── form card ────────────────────────────── */}
          <div style={{ background: '#fff', border: '1px solid #E7EAEE', borderRadius: 18, boxShadow: '0 4px 14px rgba(23,38,58,.05)', marginBottom: 24, overflow: 'hidden' }}>
            <div style={{ height: 5, background: 'linear-gradient(90deg,#FFD54F,#E6B400)' }} />
            <div style={{ padding: '26px 28px' }}>

              {/* form header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#17263A' }}>Your numbers</div>
                  <div style={{ color: '#6E7888', fontSize: 13, marginTop: 3 }}>Enter in AED or USD. Nothing is shared.</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6E7888', textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 5 }}>Currency</div>
                  <select
                    value={currency}
                    onChange={e => setCurrency(e.target.value as 'AED' | 'USD')}
                    style={{ appearance: 'none' as const, border: '1.5px solid #E7EAEE', background: '#FFF6DC', borderRadius: 10, padding: '9px 36px 9px 13px', fontWeight: 800, fontSize: 14, color: '#17263A', cursor: 'pointer', fontFamily: 'inherit', minWidth: 110 }}
                  >
                    <option value="AED">AED</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              {/* 4 inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="fhc-grid">
                <Field num="1" label="Cash in bank today"      val={cashStr} onChange={setCashStr} sym={currency} eqv={eqv(cashStr)} />
                <Field num="2" label="Average monthly revenue" val={avgStr}  onChange={setAvgStr}  sym={currency} eqv={eqv(avgStr)} />
                <Field num="3" label="Average monthly expenses" val={expStr} onChange={setExpStr}  sym={currency} eqv={eqv(expStr)} />
                <Field num="4" label="This month's revenue"    val={crStr}  onChange={setCrStr}   sym={currency} eqv={eqv(crStr)} />
              </div>

              {/* submit */}
              <div style={{ marginTop: 22, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={handleCalc}
                  style={{ background: '#FFD54F', color: '#17263A', border: 'none', borderRadius: 12, fontFamily: 'inherit', fontWeight: 700, fontSize: 15, padding: '13px 24px', cursor: 'pointer', boxShadow: '0 6px 16px rgba(230,180,0,.30)', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  Get my Financial Health Score →
                </button>
                <span style={{ color: '#6E7888', fontSize: 12.5 }}>"This month's revenue" vs your average sets your trend.</span>
              </div>
            </div>
          </div>

          {/* ── results ──────────────────────────────── */}
          {result && (
            <div ref={resultRef} style={{ background: '#fff', border: '1px solid #E7EAEE', borderRadius: 18, boxShadow: '0 4px 14px rgba(23,38,58,.05)', overflow: 'hidden', animation: 'fhcFadeIn .4s ease' }}>
              <div style={{ height: 5, background: col }} />
              <div style={{ padding: '26px 28px' }}>

                {/* score row */}
                <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 32, alignItems: 'center', marginBottom: 28 }} className="fhc-res">
                  {/* gauge */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <div style={{ position: 'relative', width: 190, height: 190 }}>
                      <svg width="190" height="190" viewBox="0 0 210 210" style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx="105" cy="105" r="88" fill="none" stroke="#E7EAEE" strokeWidth="16" />
                        <circle
                          cx="105" cy="105" r="88" fill="none"
                          stroke={col} strokeWidth="16" strokeLinecap="round"
                          strokeDasharray={CIRC} strokeDashoffset={arcOff}
                          style={{ transition: 'stroke 0.4s' }}
                        />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <b style={{ fontSize: 50, fontWeight: 900, color: '#17263A', letterSpacing: '-0.03em', lineHeight: 1 }}>{dispScore}</b>
                        <span style={{ fontSize: 12, color: '#6E7888', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginTop: 2 }}>/ 100</span>
                      </div>
                    </div>
                    <div style={{ marginTop: 12, display: 'inline-block', padding: '7px 18px', borderRadius: 999, fontWeight: 800, fontSize: 14.5, color: '#fff', background: col }}>
                      {result.band}{result.preRev ? ' · Pre-revenue' : ''}
                    </div>
                    <div style={{ marginTop: 8, color: '#42505F', fontWeight: 600, fontSize: 13.5 }}>
                      {STATUS[result.band]}
                    </div>
                  </div>

                  {/* vitals + bars */}
                  <div>
                    {/* vitals */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }} className="fhc-vitals">
                      {[
                        {
                          k: 'Runway',
                          v: result.profitable ? 'Profitable' : (isFinite(result.runway) ? `${result.runway.toFixed(1)} mo` : '—'),
                        },
                        {
                          k: 'Net burn / mo',
                          v: result.profitable
                            ? <span>+{fmt(result.avgInput - result.expInput)} {currency === 'AED' ? <Dh/> : '$'}</span>
                            : <span>{fmt(result.netBurn)} {currency === 'AED' ? <Dh/> : '$'}</span>,
                        },
                        {
                          k: 'Cash reserve',
                          v: `${isFinite(result.cashMonths) ? result.cashMonths.toFixed(1) : '—'} mo of exp`,
                        },
                      ].map((vital, i) => (
                        <div key={i} style={{ background: '#F5F6F8', border: '1px solid #E7EAEE', borderRadius: 12, padding: '13px 15px' }}>
                          <div style={{ fontSize: 11, color: '#6E7888', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{vital.k}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 3, color: '#17263A' }}>{vital.v}</div>
                        </div>
                      ))}
                    </div>

                    {/* bars */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <ProgressBar label="Runway"       pct={result.runwayScore} color={col} animate={barsOn} />
                      {!result.preRev && result.revScore !== null && (
                        <ProgressBar label="Revenue Health" pct={result.revScore} color={col} animate={barsOn} />
                      )}
                      <ProgressBar label="Cash Health"  pct={result.cashHealth}  color={col} animate={barsOn} />
                    </div>
                  </div>
                </div>

                {/* strengths / risks / improve */}
                {insights && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, paddingTop: 24, borderTop: '1px solid #E7EAEE' }} className="fhc-sri">
                    {([
                      { title: 'Strengths', items: insights.S, col: '#15824E',
                        icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#15824E" strokeWidth="2.6"><path d="M5 13l4 4L19 7"/></svg> },
                      { title: 'Risks', items: insights.R, col: '#D7263D',
                        icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#D7263D" strokeWidth="2.6"><path d="M12 8v5M12 17h.01"/><circle cx="12" cy="12" r="9"/></svg> },
                      { title: 'Areas to improve', items: insights.I, col: '#1976d2',
                        icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2.6"><path d="M5 12h14M13 6l6 6-6 6"/></svg> },
                    ] as const).map((section, i) => (
                      <div key={i}>
                        <div style={{ fontSize: 12, textTransform: 'uppercase' as const, letterSpacing: '0.06em', fontWeight: 700, color: section.col, marginBottom: 10 }}>
                          {section.title}
                        </div>
                        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
                          {section.items.map((item, j) => (
                            <li key={j} style={{ fontSize: 13, color: '#42505F', fontWeight: 500, display: 'flex', gap: 8, lineHeight: 1.45, alignItems: 'flex-start' }}>
                              <span style={{ flexShrink: 0, marginTop: 1 }}>{section.icon}</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* CTA */}
                <div style={{ marginTop: 24, background: '#10243b', borderRadius: 14, padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <b style={{ color: '#fff', fontSize: 16, fontWeight: 800, display: 'block' }}>Want help acting on this?</b>
                    <span style={{ color: '#aeb8c4', fontSize: 13 }}>Talk to the Conncct accounting team about your books, runway and filings.</span>
                  </div>
                  <a href="/accounting" style={{ background: '#FFD54F', color: '#17263A', border: 'none', borderRadius: 11, fontFamily: 'inherit', fontWeight: 700, fontSize: 14.5, padding: '11px 20px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                    Talk to our team →
                  </a>
                </div>
                <div style={{ marginTop: 12, textAlign: 'center', color: '#6E7888', fontSize: 12.5, fontWeight: 600 }}>
                  Saved to your profile · We'll remind you to review it next month.
                </div>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes fhcFadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
          @media(max-width:860px){
            .fhc-grid    { grid-template-columns: 1fr !important; }
            .fhc-res     { grid-template-columns: 1fr !important; }
            .fhc-vitals  { grid-template-columns: 1fr 1fr !important; }
            .fhc-sri     { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </main>
    </div>
  );
}
